"use client";

import { useReactFlow } from "@xyflow/react";
import { useRef, useState } from "react";
import { useSpaceKey } from "@/lib/use-shift-key";
import { panViewport } from "@/lib/space-pan";

/** Swallows every pointer event to the canvas while Space is held, so it
 *  re-implements pan with `setViewport` itself. */
export function PanLockOverlay() {
  const spaceHeld = useSpaceKey();
  const { getViewport, setViewport } = useReactFlow();
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startViewport: ReturnType<typeof getViewport>;
  } | null>(null);
  const [dragging, setDragging] = useState(false);

  if (!spaceHeld) return null;

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startViewport: getViewport(),
    };
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    setViewport(panViewport(drag.startViewport, dx, dy));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    setDragging(false);
  };

  return (
    <div
      data-testid="pan-lock-overlay"
      className="absolute inset-0"
      style={{
        zIndex: 10000,
        cursor: dragging ? "grabbing" : "grab",
        background: "transparent",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    />
  );
}
